import {
    ReactElement,
    useMemo,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useEffect,
    useImperativeHandle
} from 'react';
import { useUpdateCompanyUserAction } from '../actions';
import {
    UIButton,
    UIFormInput,
    UIFormInputRef,
    UIIconButton,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISelectInput,
    UISelectValue,
    UIText
} from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { FullAccessCompanyUser } from '../util/company-user-access';

type DescriptionSettingsSectionProps = {
    companyUser: FullAccessCompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

export function MemberDescriptionSettingsSection(
    props: DescriptionSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;
    const description = companyUser.description;

    const { t } = useCompaniesTranslation();

    const formRef = useRef<MemberDescriptionSettingsSectionFormRef>(null);
    const [isEditing, setIsEditing] = useState(false);
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_description_settings_section.toast_updated'
        )
    });

    function onSubmit(data: {
        sex: string | null;
        dateOfBirth: string | null;
        race: string | null;
        height: {
            value: number;
            unit: 'CM' | 'INCH';
        } | null;
        hairColour: string | null;
        eyeColour: string | null;
        identifyingFeatures: string[];
        vehicleNumberPlate: string | null;
    }) {
        const {
            sex,
            dateOfBirth,
            race,
            height,
            hairColour,
            eyeColour,
            identifyingFeatures,
            vehicleNumberPlate
        } = data;
        updateCompanyUser
            .updateCompanyUser(companyUser.id, {
                description: {
                    sex,
                    dateOfBirth,
                    race,
                    height,
                    hairColour,
                    eyeColour,
                    identifyingFeatures,
                    vehicleNumberPlate
                }
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    const initialDoB = useMemo(() => {
        if (description.dateOfBirth) {
            const date = new Date(description.dateOfBirth);
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }
        return null;
    }, [description.dateOfBirth]);

    return (
        <MemberDescriptionSettingsSectionForm
            ref={formRef}
            canEdit={canEdit}
            editDisabledReason={editDisabledReason}
            initialSex={description.sex}
            initialDateOfBirth={initialDoB}
            initialRace={description.race}
            initialEyeColour={description.eyeColour}
            initialHairColour={description.hairColour}
            initialHeight={description.height}
            initialIdentifyingFeatures={description.identifyingFeatures}
            initialVehicleNumberPlate={description.vehicleNumberPlate}
            loading={updateCompanyUser.loading}
            onSubmit={onSubmit}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
        />
    );
}

type DescriptionSettingsSectionFormProps = {
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
    initialSex: string | null;
    initialDateOfBirth: string | null;
    initialRace: string | null;
    initialHeight: {
        value: number;
        unit: 'CM' | 'INCH';
    } | null;
    initialHairColour: string | null;
    initialEyeColour: string | null;
    initialIdentifyingFeatures: string[];
    initialVehicleNumberPlate: string | null;
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: {
        sex: string | null;
        dateOfBirth: string | null;
        race: string | null;
        height: {
            value: number;
            unit: 'CM' | 'INCH';
        } | null;
        hairColour: string | null;
        eyeColour: string | null;
        identifyingFeatures: string[];
        vehicleNumberPlate: string | null;
    }) => void;
};

type MemberDescriptionSettingsSectionFormRef = {
    resetForm: () => void;
};

const MemberDescriptionSettingsSectionForm = forwardRef<
    MemberDescriptionSettingsSectionFormRef,
    DescriptionSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        editDisabledReason,
        initialSex,
        initialDateOfBirth,
        initialRace,
        initialHeight,
        initialHairColour,
        initialEyeColour,
        initialIdentifyingFeatures,
        initialVehicleNumberPlate,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Sex
     */
    const sexRef = useRef<UIFormInputRef>(null);
    const [sex, setSex] = useState(initialSex ?? '');
    const [sexError, setSexError] = useState('');
    const checkSex = useCallback(
        (sex: string): string => {
            const trimmed = sex.trim();
            if (trimmed.length > 100) {
                return t(
                    'components.member_description_settings_section.sex_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setSexError(checkSex(sex));
        }
    }, [showRealTimeErrors, sex, checkSex]);

    /**
     * Date of birth
     */
    const dateOfBirthRef = useRef<UIFormInputRef>(null);
    const [dateOfBirth, setDateOfBirth] = useState(initialDateOfBirth ?? '');
    const [dateOfBirthError, setDateOfBirthError] = useState('');
    const checkDateOfBirth = useCallback(
        (dateOfBirth: string): string => {
            const trimmed = dateOfBirth.trim();
            if (trimmed.length === 0) {
                return '';
            }

            // Date of birth should be in the format DD/MM/YYYY
            const dateOfBirthRegex =
                /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
            if (!dateOfBirthRegex.test(trimmed)) {
                return t(
                    'components.member_description_settings_section.date_of_birth_error_format'
                );
            }
            const [day, month, year] = trimmed.split('/').map(Number);
            const date = new Date(year, month - 1, day);
            const currentDate = new Date();
            if (date > currentDate) {
                return t(
                    'components.member_description_settings_section.date_of_birth_error_future'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setDateOfBirthError(checkDateOfBirth(dateOfBirth));
        }
    }, [showRealTimeErrors, dateOfBirth, checkDateOfBirth]);

    /**
     * Race
     */
    const raceRef = useRef<UIFormInputRef>(null);
    const [race, setRace] = useState(initialRace ?? '');
    const [raceError, setRaceError] = useState('');
    const checkRace = useCallback(
        (race: string): string => {
            const trimmed = race.trim();
            if (trimmed.length > 100) {
                return t(
                    'components.member_description_settings_section.race_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setRaceError(checkRace(race));
        }
    }, [race, showRealTimeErrors, checkRace]);

    /**
     * Height
     */
    const heightUnitOptions: UISelectValue[] = [
        {
            label: t(
                'components.member_description_settings_section.height_unit_cm'
            ),
            value: 'CM'
        },
        {
            label: t(
                'components.member_description_settings_section.height_unit_inch'
            ),
            value: 'INCH'
        }
    ];
    const [heightValue, setHeightValue] = useState(
        initialHeight?.value.toString() ?? ''
    );
    const [heightUnit, setHeightUnit] = useState<UISelectValue | undefined>(
        heightUnitOptions.find((o) => o.value === initialHeight?.unit)
    );
    const [heightError, setHeightError] = useState('');
    const checkHeight = useCallback(
        (value: string, unit: 'CM' | 'INCH' | undefined): string => {
            const trimmed = value.trim();
            if (trimmed.length === 0) {
                return '';
            }

            // Check if the value is a number
            const numberValue = parseFloat(value);
            if (isNaN(numberValue)) {
                return t(
                    'components.member_description_settings_section.height_error_not_a_number'
                );
            }
            // Check if the value is positive
            if (numberValue <= 0) {
                return t(
                    'components.member_description_settings_section.height_error_not_positive'
                );
            }

            if (!unit) {
                return t(
                    'components.member_description_settings_section.height_error_unit_required'
                );
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setHeightError(
                checkHeight(
                    heightValue,
                    heightUnit?.value as 'CM' | 'INCH' | undefined
                )
            );
        }
    }, [showRealTimeErrors, heightValue, heightUnit, checkHeight]);

    /**
     * Hair Colour
     */
    const hairColourRef = useRef<UIFormInputRef>(null);
    const [hairColour, setHairColour] = useState(initialHairColour ?? '');
    const [hairColourError, setHairColourError] = useState('');
    const checkHairColour = useCallback(
        (hairColour: string): string => {
            const trimmed = hairColour.trim();
            if (trimmed.length > 100) {
                return t(
                    'components.member_description_settings_section.hair_colour_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setHairColourError(checkHairColour(hairColour));
        }
    }, [hairColour, showRealTimeErrors, checkHairColour]);

    /**
     * Eye Colour
     */
    const eyeColourRef = useRef<UIFormInputRef>(null);
    const [eyeColour, setEyeColour] = useState(initialEyeColour ?? '');
    const [eyeColourError, setEyeColourError] = useState('');
    const checkEyeColour = useCallback(
        (eyeColour: string): string => {
            const trimmed = eyeColour.trim();
            if (trimmed.length > 100) {
                return t(
                    'components.member_description_settings_section.eye_colour_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setEyeColourError(checkEyeColour(eyeColour));
        }
    }, [eyeColour, showRealTimeErrors, checkEyeColour]);

    /**
     * Identifying Features
     */
    const [identifyingFeatures, setIdentifyingFeatures] = useState(
        initialIdentifyingFeatures
    );
    const [identifyingFeaturesError, setIdentifyingFeaturesError] = useState<
        string[]
    >([]);
    const checkIdentifyingFeatures = useCallback(
        (identifyingFeatures: string[]): string[] => {
            return identifyingFeatures.map((feature) => {
                const trimmed = feature.trim();
                if (trimmed.length === 0) {
                    return t(
                        'components.member_description_settings_section.list_item_error_required'
                    );
                }
                if (trimmed.length > 100) {
                    return t(
                        'components.member_description_settings_section.list_item_error_too_long'
                    );
                }
                return '';
            });
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setIdentifyingFeaturesError(
                checkIdentifyingFeatures(identifyingFeatures)
            );
        }
    }, [identifyingFeatures, showRealTimeErrors, checkIdentifyingFeatures]);

    /**
     * Vehicle Number Plate
     */
    const vehicleNumberPlateRef = useRef<UIFormInputRef>(null);
    const [vehicleNumberPlate, setVehicleNumberPlate] = useState(
        initialVehicleNumberPlate ?? ''
    );
    const [vehicleNumberPlateError, setVehicleNumberPlateError] = useState('');
    const checkVehicleNumberPlate = useCallback(
        (vehicleNumberPlate: string): string => {
            const trimmed = vehicleNumberPlate.trim();
            if (trimmed.length > 100) {
                return t(
                    'components.member_description_settings_section.vehicle_number_plate_error_too_long'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setVehicleNumberPlateError(
                checkVehicleNumberPlate(vehicleNumberPlate)
            );
        }
    }, [vehicleNumberPlate, showRealTimeErrors, checkVehicleNumberPlate]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (
            !checkSex(sex) &&
            !checkDateOfBirth(dateOfBirth) &&
            !checkRace(race) &&
            !checkHeight(
                heightValue,
                heightUnit?.value as 'CM' | 'INCH' | undefined
            ) &&
            !checkHairColour(hairColour) &&
            !checkEyeColour(eyeColour) &&
            !checkIdentifyingFeatures(identifyingFeatures).some(
                (error) => error
            ) &&
            !checkVehicleNumberPlate(vehicleNumberPlate)
        ) {
            setShowRealTimeErrors(false);

            const sexTrimmed = sex.trim() || null;
            let dateOfBirthTrimmed = dateOfBirth.trim() || null;
            const raceTrimmed = race.trim() || null;
            let height: {
                value: number;
                unit: 'CM' | 'INCH';
            } | null = null;
            if (heightValue.trim() && heightUnit) {
                height = {
                    value: parseFloat(heightValue),
                    unit: heightUnit.value as 'CM' | 'INCH'
                };
            }
            const hairColourTrimmed = hairColour.trim() || null;
            const eyeColourTrimmed = eyeColour.trim() || null;
            const identifyingFeaturesTrimmed = identifyingFeatures
                .map((f) => f.trim())
                .filter((f) => f.length > 0);
            const vehicleNumberPlateTrimmed = vehicleNumberPlate.trim() || null;

            // We need to cast date of birth, if not null, from DD/MM/YYYY to YYYY-MM-DD
            if (dateOfBirthTrimmed) {
                const [day, month, year] = dateOfBirthTrimmed
                    .split('/')
                    .map(Number);
                if (day && month && year) {
                    dateOfBirthTrimmed = `${year}-${month
                        .toString()
                        .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                }
            }

            onSubmit?.({
                sex: sexTrimmed,
                dateOfBirth: dateOfBirthTrimmed,
                race: raceTrimmed,
                height,
                hairColour: hairColourTrimmed,
                eyeColour: eyeColourTrimmed,
                identifyingFeatures: identifyingFeaturesTrimmed,
                vehicleNumberPlate: vehicleNumberPlateTrimmed
            });
        }
    };

    function resetForm() {
        setSex(initialSex ?? '');
        setSexError('');
        setDateOfBirth(initialDateOfBirth ?? '');
        setDateOfBirthError('');
        setRace(initialRace ?? '');
        setRaceError('');
        setHeightValue(initialHeight?.value.toString() ?? '');
        setHeightUnit(
            heightUnitOptions.find((o) => o.value === initialHeight?.unit)
        );
        setHeightError('');
        setHairColour(initialHairColour ?? '');
        setHairColourError('');
        setEyeColour(initialEyeColour ?? '');
        setEyeColourError('');
        setIdentifyingFeatures(initialIdentifyingFeatures);
        setIdentifyingFeaturesError(
            checkIdentifyingFeatures(initialIdentifyingFeatures)
        );
        setVehicleNumberPlate(initialVehicleNumberPlate ?? '');
        setVehicleNumberPlateError('');
        setShowRealTimeErrors(false);

        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t(
                        'components.member_description_settings_section.title'
                    )}
                    rightButtons={
                        isEditing
                            ? []
                            : [
                                  {
                                      text: t('shared.labels.edit'),
                                      variant: 'textDefault',
                                      type: 'button',
                                      disabled: !canEdit,
                                      disabledTooltip: editDisabledReason,
                                      onClick: () => {
                                          setIsEditing(true);
                                      }
                                  }
                              ]
                    }
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <Description />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.sex_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.sex_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={sexRef}
                                    name="Sex"
                                    value={sex}
                                    placeholder={t(
                                        'components.member_description_settings_section.sex_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={sexError}
                                    required
                                    maxLength={100}
                                    onChange={setSex}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.date_of_birth_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.date_of_birth_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={dateOfBirthRef}
                                    name="Date of Birth"
                                    value={dateOfBirth}
                                    placeholder={t(
                                        'components.member_description_settings_section.date_of_birth_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={dateOfBirthError}
                                    required
                                    maxLength={10}
                                    onChange={setDateOfBirth}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.race_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.race_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={raceRef}
                                    name="Race"
                                    value={race}
                                    placeholder={t(
                                        'components.member_description_settings_section.race_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={raceError}
                                    required
                                    maxLength={100}
                                    onChange={setRace}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.height_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.height_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    gap: 16
                                }}
                            >
                                <div style={{ display: 'flex', flex: 2 }}>
                                    <UIFormInput
                                        name="Height"
                                        value={heightValue}
                                        placeholder={t(
                                            'components.member_description_settings_section.height_placeholder'
                                        )}
                                        type="text"
                                        autoComplete="on"
                                        errorText={heightError}
                                        required
                                        maxLength={100}
                                        onChange={setHeightValue}
                                        disabled={!isEditing}
                                    />
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flex: 1
                                    }}
                                >
                                    <UISelectInput
                                        required={false}
                                        placeholder={t(
                                            'components.member_description_settings_section.height_unit_placeholder'
                                        )}
                                        options={heightUnitOptions}
                                        value={heightUnit}
                                        onChange={setHeightUnit}
                                        disabled={!isEditing}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.hair_colour_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.hair_colour_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={hairColourRef}
                                    name="Hair Colour"
                                    value={hairColour}
                                    placeholder={t(
                                        'components.member_description_settings_section.hair_colour_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={hairColourError}
                                    required
                                    maxLength={100}
                                    onChange={setHairColour}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.eye_colour_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.eye_colour_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={eyeColourRef}
                                    name="Eye Colour"
                                    value={eyeColour}
                                    placeholder={t(
                                        'components.member_description_settings_section.eye_colour_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={eyeColourError}
                                    maxLength={100}
                                    onChange={setEyeColour}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.identifying_features_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.identifying_features_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 16
                                }}
                            >
                                {identifyingFeatures.map((feature, index) => (
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: 16,
                                            alignItems: 'center'
                                        }}
                                    >
                                        <UIFormInput
                                            name={`Feature ${index + 1}`}
                                            value={`${isEditing ? '' : `${index + 1}. `}${feature}`}
                                            placeholder={t(
                                                'components.member_description_settings_section.identifying_feature_placeholder',
                                                { number: index + 1 }
                                            )}
                                            type="text"
                                            autoComplete="on"
                                            errorText={
                                                identifyingFeaturesError[index]
                                            }
                                            maxLength={100}
                                            onChange={(value) => {
                                                setIdentifyingFeatures(
                                                    (prev) => {
                                                        const newFeatures = [
                                                            ...prev
                                                        ];
                                                        newFeatures[index] =
                                                            value;
                                                        return newFeatures;
                                                    }
                                                );
                                            }}
                                            disabled={!isEditing}
                                        />

                                        {isEditing && (
                                            <UIIconButton
                                                icon="trash-2"
                                                iconColor="white"
                                                variant="danger"
                                                type="button"
                                                onClick={() => {
                                                    setIdentifyingFeatures(
                                                        (prev) =>
                                                            prev.filter(
                                                                (_, i) =>
                                                                    i !== index
                                                            )
                                                    );
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                                {isEditing &&
                                    identifyingFeatures.length < 20 && (
                                        <div>
                                            <UIButton
                                                variant="textPrimary"
                                                text={t(
                                                    'components.member_description_settings_section.identifying_features_add_button'
                                                )}
                                                rightIcon="plus-circle"
                                                type="button"
                                                onClick={() => {
                                                    setIdentifyingFeatures(
                                                        (prev) => [...prev, '']
                                                    );
                                                }}
                                            />
                                        </div>
                                    )}
                                {!isEditing &&
                                    identifyingFeatures.length == 0 && (
                                        <UIFormInput
                                            value={t('shared.labels.none')}
                                            disabled={true}
                                        />
                                    )}
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_description_settings_section.vehicle_number_plate_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_description_settings_section.vehicle_number_plate_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={vehicleNumberPlateRef}
                                    name="Vehicle Number Plate"
                                    value={vehicleNumberPlate}
                                    placeholder={t(
                                        'components.member_description_settings_section.vehicle_number_plate_placeholder'
                                    )}
                                    type="text"
                                    autoComplete="on"
                                    errorText={vehicleNumberPlateError}
                                    maxLength={100}
                                    onChange={setVehicleNumberPlate}
                                    disabled={!isEditing}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                {isEditing && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                type: 'button',
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});

function Description(): ReactElement {
    const { t } = useCompaniesTranslation();

    return (
        <UIText type="default2">
            {t('components.member_description_settings_section.intro')}
        </UIText>
    );
}
